import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X, User } from 'lucide-react';
import { apiGet } from '../lib/api';

/**
 * Search-and-select picker for Employees.
 *
 * Matches by Name OR Emp ID (substring, case-insensitive).
 * Stores selection as plain string: "<Name>" (so existing CRM owner fields keep working).
 *
 * Props:
 *   value           — current selected value (string)
 *   onChange        — callback(string)
 *   placeholder     — input placeholder
 *   allowFreeText   — allow typing arbitrary text (default true; turn off to force a pick)
 *   restrictToTeam  — when true, the picker only shows the current user's team (self + downstream).
 *                     super_admin/MD always see all employees regardless of this flag.
 *                     Use for record-owner reassignment so a BD Manager can only assign to their reports.
 *   testid          — automation hook
 */
const _empCache = { all: null, team: null };

async function fetchEmployees(restrictToTeam = false) {
  const cacheKey = restrictToTeam ? 'team' : 'all';
  if (_empCache[cacheKey]) return _empCache[cacheKey];
  try {
    if (restrictToTeam) {
      const res = await apiGet('/employees/my-team');
      _empCache.team = (res.employees || []).map(e => ({
        id: e.id, name: e.name, emp_id: e.emp_id, department: e.department, designation: e.designation,
      }));
      return _empCache.team;
    }
    const list = await apiGet('/hr/employees');
    _empCache.all = (list || []).map(e => ({
      id: e.id, name: e.name, emp_id: e.emp_id, department: e.department, designation: e.designation,
    }));
    return _empCache.all;
  } catch {
    return [];
  }
}

export function clearEmployeePickerCache() {
  _empCache.all = null;
  _empCache.team = null;
}

export default function EmployeePicker({
  value = '',
  onChange,
  placeholder = 'Search by name or emp ID…',
  allowFreeText = true,
  restrictToTeam = false,
  testid,
  required = false,
}) {
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || '');
  const [activeIdx, setActiveIdx] = useState(0);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchEmployees(restrictToTeam).then(setEmployees);
  }, [restrictToTeam]);
  useEffect(() => { setQuery(value || ''); }, [value]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = !q
    ? employees.slice(0, 30)
    : employees.filter(e =>
        (e.name || '').toLowerCase().includes(q) ||
        (e.emp_id || '').toLowerCase().includes(q)
      ).slice(0, 30);

  const commit = (emp) => {
    const v = emp.name;
    setQuery(v);
    setOpen(false);
    // Pass (name, id, emp) — callers that only read the first arg keep working,
    // and forms that need emp_id (e.g. HRLetters) can use the second/third args.
    onChange?.(v, emp.id, emp);
  };

  const handleKey = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(filtered.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[activeIdx]) commit(filtered[activeIdx]);
      else if (allowFreeText) { onChange?.(query); setOpen(false); }
    }
    else if (e.key === 'Escape') setOpen(false);
  };

  const clear = () => {
    setQuery('');
    onChange?.('');
    inputRef.current?.focus();
  };

  return (
    <div ref={rootRef} className="relative" data-testid={testid}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          required={required}
          onFocus={() => setOpen(true)}
          onChange={e => {
            const v = e.target.value;
            setQuery(v);
            setOpen(true);
            setActiveIdx(0);
            if (allowFreeText) onChange?.(v);
          }}
          onKeyDown={handleKey}
          placeholder={placeholder}
          className="w-full pl-9 pr-9 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-[#1F7A6E]"
          data-testid={testid ? `${testid}-input` : undefined}
          autoComplete="off"
        />
        {query ? (
          <button type="button" onClick={clear} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
            <X size={14} />
          </button>
        ) : (
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        )}
      </div>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-4 text-xs text-slate-500 text-center">
              <User size={14} className="mx-auto mb-1 text-slate-300" />
              No employees match "{query}"
              {allowFreeText && query.trim() && (
                <div className="mt-1 text-[10px] text-slate-400">Press Enter to keep this as a free-text entry.</div>
              )}
            </div>
          ) : filtered.map((e, idx) => (
            <button
              key={e.id}
              type="button"
              onMouseEnter={() => setActiveIdx(idx)}
              onClick={() => commit(e)}
              className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm ${idx === activeIdx ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
              data-testid={testid ? `${testid}-option-${e.emp_id}` : undefined}
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-800 truncate">{e.name}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wide">
                  {e.emp_id}{e.designation ? ` · ${e.designation}` : ''}{e.department ? ` · ${e.department}` : ''}
                </div>
              </div>
              {query.toLowerCase() === e.name.toLowerCase() && <Check size={14} className="text-emerald-600 flex-shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
